import React from 'react';
import { Info, Coins, Calculator, PieChart, Zap, AlertTriangle, Shield, BarChart2 } from 'lucide-react';

/**
 * Компонент с информацией о криптовалютных инвестициях и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о криптовалютных инвестициях
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Coins className="w-5 h-5 text-blue-500" />
          Что такое криптовалюты?
        </h3>
        <p>
          <strong>Криптовалюта</strong> — это цифровой актив, созданный с использованием криптографических методов, который функционирует на основе технологии блокчейн. Криптовалюты децентрализованы и обычно не зависят от центральных банков или правительств. Наиболее известными примерами являются Bitcoin (BTC), Ethereum (ETH), Binance Coin (BNB) и многие другие.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Ключевые особенности криптовалютных инвестиций
        </h3>
        <ul>
          <li><strong>Высокая волатильность:</strong> Криптовалюты известны значительными колебаниями цен, которые могут приносить как высокую прибыль, так и существенные убытки.</li>
          <li><strong>Децентрализация:</strong> Большинство криптовалют не контролируются единым центром, что исключает возможность манипуляций со стороны центрального органа.</li>
          <li><strong>Круглосуточная торговля:</strong> Криптовалютный рынок работает 24/7 без выходных и праздников.</li>
          <li><strong>Низкий порог входа:</strong> Инвестировать в криптовалюты можно с относительно небольших сумм.</li>
          <li><strong>Технологические инновации:</strong> Многие криптопроекты представляют собой инновационные технологии, потенциально способные изменить различные отрасли.</li>
        </ul>

        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800 my-6">
          <h4 className="text-indigo-700 dark:text-indigo-300 font-medium">Зачем диверсифицировать криптопортфель?</h4>
          <p className="text-indigo-600 dark:text-indigo-400 text-sm mb-0">
            Распределение инвестиций между разными криптовалютами позволяет снизить риски. Если один актив потеряет в стоимости, другие могут показать рост, что поможет сбалансировать общий результат инвестиций.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <PieChart className="w-5 h-5 text-green-500" />
          Основные стратегии инвестирования в криптовалюты
        </h3>
        <ul>
          <li>
            <strong>HODL (Hold On for Dear Life):</strong> Долгосрочное удержание криптовалюты независимо от краткосрочных колебаний рынка. Эта стратегия основана на вере в долгосрочный потенциал технологии и роста стоимости.
          </li>
          <li>
            <strong>Dollar Cost Averaging (DCA):</strong> Регулярная покупка криптовалюты на фиксированную сумму вне зависимости от текущей цены. Это позволяет усреднить стоимость входа и снизить влияние волатильности.
          </li>
          <li>
            <strong>Активный трейдинг:</strong> Частая покупка и продажа криптовалют для получения прибыли на краткосрочных колебаниях цены. Требует значительных знаний, опыта и времени.
          </li>
          <li>
            <strong>Стейкинг:</strong> Размещение криптовалют для поддержки работы блокчейн-сети и получения вознаграждения в виде дополнительных монет.
          </li>
          <li>
            <strong>Лендинг:</strong> Предоставление криптовалюты в заем через специализированные платформы для получения процентного дохода.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-orange-500" />
          Анализ криптовалютных активов
        </h3>
        <p>
          Перед инвестированием важно провести комплексный анализ криптовалютного актива:
        </p>
        <ul>
          <li>
            <strong>Фундаментальный анализ:</strong> Оценка технологии, команды разработчиков, общей концепции проекта, дорожной карты, партнерств и потенциального применения.
          </li>
          <li>
            <strong>Технический анализ:</strong> Изучение графиков цен, объемов торгов и индикаторов для прогнозирования будущих движений рынка.
          </li>
          <li>
            <strong>Анализ сетевых показателей:</strong> Изучение активности блокчейна, количества активных адресов, комиссий, хешрейта и других параметров сети.
          </li>
          <li>
            <strong>Рыночный анализ:</strong> Оценка рыночной капитализации, ликвидности, позиции относительно конкурентов и общего рыночного тренда.
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Признаки потенциального мошенничества в криптопроектах
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Обещания нереально высокой гарантированной прибыли</li>
            <li>Отсутствие детальной технической документации (whitepaper)</li>
            <li>Анонимная или малоизвестная команда разработчиков</li>
            <li>Агрессивный маркетинг и давление на быстрое решение об инвестициях</li>
            <li>Централизованный контроль над проектом при декларировании децентрализации</li>
            <li>Низкая активность в репозиториях с открытым кодом (если проект открытый)</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-500" />
          Безопасность криптовалютных инвестиций
        </h3>
        <p>
          Обеспечение безопасности — ключевой аспект инвестирования в криптовалюты. Следует придерживаться следующих правил:
        </p>
        <ul>
          <li><strong>Надежное хранение:</strong> Используйте аппаратные кошельки (Ledger, Trezor) для хранения значительных сумм.</li>
          <li><strong>Двухфакторная аутентификация (2FA):</strong> Активируйте 2FA на всех используемых криптобиржах и сервисах.</li>
          <li><strong>Диверсификация хранения:</strong> Не храните все криптоактивы на одной платформе или в одном кошельке.</li>
          <li><strong>Проверка адресов:</strong> Всегда проверяйте адреса кошельков перед отправкой средств.</li>
          <li><strong>Выбор бирж:</strong> Используйте только проверенные биржи с хорошей репутацией и высоким уровнем безопасности.</li>
          <li><strong>Резервные копии:</strong> Создавайте и безопасно храните резервные копии ключей и фраз восстановления кошельков.</li>
          <li><strong>Обновления ПО:</strong> Поддерживайте программное обеспечение кошельков и устройств в актуальном состоянии.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-purple-500" />
          Особенности криптовалютного рынка в России
        </h3>
        <p>
          Российский криптовалютный рынок имеет свои специфические черты:
        </p>
        <ul>
          <li>
            <strong>Правовое регулирование:</strong> С 2021 года в России действует Федеральный закон "О цифровых финансовых активах", который признает существование криптовалют, но запрещает их использование для оплаты товаров и услуг.
          </li>
          <li>
            <strong>Налогообложение:</strong> Доходы от операций с криптовалютами облагаются НДФЛ по ставке 13% как иное имущество.
          </li>
          <li>
            <strong>Доступ к биржам:</strong> Многие международные криптобиржи ограничили доступ для российских пользователей, что привело к росту популярности P2P-платформ и локальных решений.
          </li>
          <li>
            <strong>Майнинг:</strong> Россия является одним из крупных центров майнинга криптовалют благодаря относительно низким ценам на электроэнергию и холодному климату в некоторых регионах.
          </li>
          <li>
            <strong>Инвестиционный интерес:</strong> Несмотря на законодательные ограничения, интерес к криптовалютам среди российских инвесторов остается высоким, особенно в периоды экономической нестабильности.
          </li>
        </ul>

        <h3>Как начать инвестировать в криптовалюты в России</h3>
        <ol>
          <li>
            <strong>Изучите основы:</strong> Понимание технологии блокчейн, принципов работы криптовалют и основных инвестиционных стратегий.
          </li>
          <li>
            <strong>Выберите платформу:</strong> Определитесь с криптобиржей или сервисом, через который будете покупать криптовалюты (с учетом доступности для российских пользователей).
          </li>
          <li>
            <strong>Создайте кошелек:</strong> Заведите криптовалютный кошелек для хранения активов (горячий или холодный).
          </li>
          <li>
            <strong>Пройдите верификацию:</strong> Выполните процедуру KYC (Know Your Customer) на выбранной платформе, если это требуется.
          </li>
          <li>
            <strong>Совершите первую покупку:</strong> Начните с небольшой суммы, чтобы освоиться с процессом покупки и хранения криптовалюты.
          </li>
          <li>
            <strong>Разработайте стратегию:</strong> Определите свои инвестиционные цели, временной горизонт и стратегию (HODL, DCA или другую).
          </li>
          <li>
            <strong>Соблюдайте налоговые обязательства:</strong> Ведите учет операций и декларируйте доходы от операций с криптовалютами.
          </li>
        </ol>

        <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg my-6">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Популярные криптовалюты для инвестирования</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">Для начинающих инвесторов:</h5>
              <ul className="text-sm text-gray-600 dark:text-gray-400">
                <li><strong>Bitcoin (BTC)</strong> — первая и самая известная криптовалюта</li>
                <li><strong>Ethereum (ETH)</strong> — платформа для смарт-контрактов и децентрализованных приложений</li>
                <li><strong>Binance Coin (BNB)</strong> — нативный токен популярной биржи Binance</li>
                <li><strong>Stablecoins (USDT, USDC)</strong> — криптовалюты, привязанные к фиатным валютам</li>
              </ul>
            </div>
            <div>
              <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">Для опытных инвесторов:</h5>
              <ul className="text-sm text-gray-600 dark:text-gray-400">
                <li><strong>Solana (SOL)</strong> — высокопроизводительная блокчейн-платформа</li>
                <li><strong>Polkadot (DOT)</strong> — протокол для связи разных блокчейнов</li>
                <li><strong>Cardano (ADA)</strong> — научно обоснованная блокчейн-платформа</li>
                <li><strong>Avalanche (AVAX)</strong> — платформа для DeFi и децентрализованных приложений</li>
              </ul>
            </div>
          </div>
        </div>

        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам оценить потенциальную доходность инвестиций в различные криптовалюты с учетом различных сценариев и стратегий. Используйте его как инструмент планирования, но помните о высоком уровне неопределенности криптовалютного рынка.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;